'use client'

// FirstPersonController — walk the world in first person.
//
// Controls (per the design): W/S move along facing, A/D TURN (not strafe),
// mouse looks (pointer lock). Ground-follows the terrain heightfield, slides
// along obstacle AABBs (the same colliders the kiwi uses, plus the container
// nests), and can't walk through Victor. Adds a subtle walk head-bob (camera
// shake) and triggers footstep audio at each step.
//
// Reuses useObstacles() (parsed from obstacles.glb) + useTerrainHeight() so it
// shares exactly the collision + ground data the server-authoritative kiwi
// already respects. Mount inside ObstacleProvider + TerrainHeightProvider.

import { useEffect, useRef } from 'react'
import { useThree, useFrame } from '@react-three/fiber'
import { useObstacles } from '../obstacles/ObstacleProvider'
import { useTerrainHeight } from '../terrain/TerrainHeightProvider'
import useGameStore from '../../../stores/gameStore'
import { SHRINES } from '../../../lib/world/sceneConfig'
import { playFootstep, unlockAudio, loadFootsteps } from '../../../lib/audio/footsteps'
import { sendPlayerSignal, SIGNAL_MARKS } from '../../../lib/signalSend'
import { FEATURES } from '../../../lib/features'
import { touchInput, drainLook } from '../../../lib/input/touchInput'
import { aimsAtBird, openBag, tookBird } from './BirdHit'
import { liftsNow, inCar, shutDoors, liftAsk, callLift } from '../../../lib/world/liftClient'

// --- feel / tuning ---
const EYE_HEIGHT = 1.7
const PLAYER_RADIUS = 0.35   // was 0.45; slimmer so tight gaps are passable
const WALK_SPEED = 5.5          // units/sec
const TURN_SPEED = 2.6          // radians/sec (A/D)
const MOUSE_SENS = 0.0022       // radians per pixel
const TOUCH_LOOK_SENS = 0.0034  // radians per pixel — a thumb travels less than a mouse
const PITCH_MAX = 1.45          // clamp look up/down (~83°)
const KIWI_RADIUS = 0.7         // can't get closer than this to Victor
const FP_FOV = 75

// Head-bob (camera shake while walking) + footsteps.
const BOB_RATE = 2.3            // bob phase speed factor
const BOB_AMP = 0.055           // vertical bob metres
const ROLL_AMP = 0.006          // slight roll radians
const STEP_TROUGH = -0.9        // fire a footstep when the bob dips past this

// Running: hold shift. The bob swells with the pace so the shake reads
// as effort, not as a faster metronome on the same stroll.
const RUN_MULT = 1.8
const RUN_BOB_AMP = 1.7         // bob amplitude multiplier at a run
const RUN_BOB_RATE = 1.2        // bob phase multiplier at a run
const RUN_ROLL = 2.0            // roll multiplier at a run

// Breathing: the camera is never perfectly still. A slow chest-rise on
// the Y plus a whisper of pitch, about sixteen breaths a minute; running
// deepens it. Deliberately tiny: it should be felt, not seen.
const BREATH_HZ = 0.27
const BREATH_AMP = 0.02
const BREATH_PITCH = 0.0032
const RUN_BREATH = 2.2

// The vertical world: gravity, a jump, and stepping up onto the boxes
// the ruins are built from. Collision used to be flat XZ; now a box
// whose top sits within STEP_UP of your feet is a stair, not a wall,
// and any box top is ground you can land on.
const GRAVITY = 26
const JUMP_V = 7.6              // ~1.1u of jump height
const STEP_UP = 0.55            // what counts as a stair rather than a wall
const BODY_H = 1.6              // duck under nothing shorter than this
const STEP_SMOOTH = 12          // how fast the feet ease onto a new tread
const LAND_THUD = 0.35          // fall further than this and the landing shakes

// Wall-bump feedback + crystal aiming.
const BUMP_TIME = 0.24          // seconds of impact shake
const AIM_MAX_DIST = 14         // how far a crystal can be read from
const AIM_MIN_DOT = 0.985       // ~10 degree cone at range

// Spawn back from the shrine, looking at it: the shrine and the ruins behind
// it are the centrepiece, so you should open on them rather than with your
// back to them. Camera faces -Z at yaw 0, and the shrine sits at -Z of spawn.
//
// 18 -> 28 on 24 Aug. At 18 the cat statue filled the frame with its head
// and ears cut off above the viewport, so the first thing anybody saw was
// an unreadable white shape. Ten units back puts the whole statue in the
// opening shot with the ruins behind it, which is the picture the place
// is actually built around.
const SPAWN_X = SHRINES.center[0]
// (25.1 from the cat itself: it stood 2.9 in front of the shrine point when this was measured, and is on it now)
const SPAWN_Z = SHRINES.center[2] + 25.1
const SPAWN_YAW = 0              // face -Z (toward the shrine / ruins)

// ?spawn=x,z drops you somewhere else instead. Deep-links into the far
// corners: handy for checking work at the pond without the walk, and for
// pointing people at a spot while something is happening there.
function spawnOverride() {
  if (typeof window === 'undefined') return null
  const raw = new URLSearchParams(window.location.search).get('spawn')
  if (!raw) return null
  const [x, z] = raw.split(',').map(Number)
  if (!Number.isFinite(x) || !Number.isFinite(z)) return null
  return { x, z }
}

// Hard walk limit: the visual floor is a 250-unit square, so past ~125 the
// heightfield clamps to its border value and you'd stride into the void on
// invisible ground forever. Kept a little inside the edge.
const WALK_LIMIT = 120

export function FirstPersonController({ enabled }) {
  const { camera, gl } = useThree()
  const { obstacles, noGoZones, walkables, visitorOnly } = useObstacles()
  const terrain = useTerrainHeight()

  // Persistent walk state (survives camera<->first-person toggles).
  const st = useRef({
    x: SPAWN_X, z: SPAWN_Z, yaw: SPAWN_YAW, pitch: 0,
    bob: 0, prevBobSin: 0, initialized: false,
    bumpT: 0, wasBlocked: false, aimTimer: 0,
    // feet height, vertical speed, and whether anything is under them
    py: 0, vy: 0, grounded: true, settled: false, fallFrom: 0, breathT: 0,
  })
  // What the crosshair is currently over (offering crystal), mirrored into
  // gameStore (throttled, on change only) for the DOM crosshair label.
  const aimRef = useRef(null)
  const keys = useRef({ f: false, b: false, l: false, r: false, run: false, jump: false })
  const locked = useRef(false)

  // Flattened AABB collider list: the GLB is the single source of truth for
  // collision (obstacles + no-go zones), so both the player and the kiwi
  // (which gets these same boxes over the WS) agree with what's in Blender.
  const colliders = useRef([])
  useEffect(() => {
    // ORIENTED boxes (match the rotated building), not the enclosing AABB.
    // ca/sa are the box's Y-rotation, taken from its axisX basis so world<->
    // local transforms are a couple of multiplies with no per-frame trig.
    const toOBB = (c, solid) => {
      if (!c.center || !c.halfExtents || !c.axisX) return null
      let ca = c.axisX.x, sa = -c.axisX.z
      const len = Math.hypot(ca, sa) || 1
      return {
        cx: c.center.x, cz: c.center.z, hx: c.halfExtents.x, hz: c.halfExtents.z,
        ca: ca / len, sa: sa / len,
        // the vertical span, for standing on tops and stepping over stairs
        topY: c.center.y + c.halfExtents.y,
        bottomY: c.center.y - c.halfExtents.y,
        // walkables support the feet but block nobody
        solid,
      }
    }
    const list = []
    for (const o of obstacles || []) { const b = toOBB(o, true); if (b) list.push(b) }
    for (const z of noGoZones || []) { const b = toOBB(z, true); if (b) list.push(b) }
    for (const w of walkables || []) { const b = toOBB(w, false); if (b) list.push(b) }
    // the monorail's beams to walk along, and what stops you in its tunnels:
    // a visitor's alone, never under the bird
    for (const o of visitorOnly?.solids || []) { const b = toOBB(o, true); if (b) list.push(b) }
    for (const w of visitorOnly?.floors || []) { const b = toOBB(w, false); if (b) list.push(b) }
    colliders.current = list
  }, [obstacles, noGoZones, walkables, visitorOnly])
  // the monorail lift's doors while they are shut: walls that come and go
  const doors = useRef([])

  // Input listeners — only while enabled. Pointer lock engages on click.
  useEffect(() => {
    if (!enabled) return
    const dom = gl.domElement

    const onClick = () => {
      unlockAudio()
      loadFootsteps()
      if (document.pointerLockElement !== dom) {
        // A tap on him that has just opened his bag (BirdHit) is not also a
        // click into the world: taking the mouse then would leave it locked
        // under the open pop-up.
        if (tookBird()) return
        try { dom.requestPointerLock() } catch { /* ignore */ }
        return
      }
      // Locked click: him under the crosshair opens his bag, or the offering
      // under it opens, whichever is nearer. One ray at him, on the click
      // only. Exiting pointer lock hands the cursor to the DOM panel;
      // clicking the canvas afterwards re-locks and walking resumes.
      const aim = aimRef.current
      const ent = aim ? useGameStore.getState().entityStates.find((e) => e.id === aim.id) : null
      const birdAt = aimsAtBird(camera)
      const offeringAt = ent ? Math.hypot(ent.pos.x - st.current.x, ent.pos.z - st.current.z) : Infinity
      if (birdAt !== null && birdAt <= offeringAt) {
        if (openBag()) document.exitPointerLock()
        return
      }
      if (ent) {
        useGameStore.getState().setOpenedOffering({ id: ent.id, ...ent.state })
        document.exitPointerLock()
      }
    }
    const onLockChange = () => { locked.current = document.pointerLockElement === dom }
    const onMouseMove = (e) => {
      if (!locked.current) return
      st.current.yaw -= e.movementX * MOUSE_SENS
      st.current.pitch -= e.movementY * MOUSE_SENS
      st.current.pitch = Math.max(-PITCH_MAX, Math.min(PITCH_MAX, st.current.pitch))
    }
    const isTyping = (e) => e.target && /INPUT|TEXTAREA|SELECT/.test(e.target.tagName)
    const onKeyDown = (e) => {
      if (isTyping(e)) return
      // Signals: E whistles anywhere; 1-5 send a mark while the bar is up
      // (proximity to Victor opens it, see the frame loop). Cooldown lives
      // in sendPlayerSignal. Behind the flag with the rest of saying hello:
      // hiding the strip alone would have left E still whistling at him.
      if (FEATURES.signals) {
        if (e.code === 'KeyE') {
          sendPlayerSignal('whistle', { x: st.current.x, z: st.current.z })
          return
        }
        if (useGameStore.getState().signalBarOpen) {
          const di = ['Digit1', 'Digit2', 'Digit3', 'Digit4', 'Digit5'].indexOf(e.code)
          if (di >= 0) {
            sendPlayerSignal(SIGNAL_MARKS[di], { x: st.current.x, z: st.current.z })
            return
          }
        }
      }
      // L: the monorail's lift, by its doors or inside it (F flies the drone)
      if (e.code === 'KeyL') {
        const ask = liftAsk(st.current.x, st.current.py, st.current.z)
        if (ask) { callLift(ask); return }
      }
      switch (e.code) {
        case 'KeyW': case 'ArrowUp':   keys.current.f = true; break
        case 'KeyS': case 'ArrowDown': keys.current.b = true; break
        case 'KeyA': case 'ArrowLeft':  keys.current.l = true; break
        case 'KeyD': case 'ArrowRight': keys.current.r = true; break
        case 'ShiftLeft': case 'ShiftRight': keys.current.run = true; break
        case 'Space':
          // stop the page scrolling under the world
          e.preventDefault()
          keys.current.jump = true
          break
        default: return
      }
    }
    const onKeyUp = (e) => {
      switch (e.code) {
        case 'KeyW': case 'ArrowUp':   keys.current.f = false; break
        case 'KeyS': case 'ArrowDown': keys.current.b = false; break
        case 'KeyA': case 'ArrowLeft':  keys.current.l = false; break
        case 'KeyD': case 'ArrowRight': keys.current.r = false; break
        case 'ShiftLeft': case 'ShiftRight': keys.current.run = false; break
        case 'Space':                  keys.current.jump = false; break
        default: return
      }
    }

    // Focus loss (alt-tab, OS overlay) never delivers the keyup, which left
    // the player walking into a wall until refocus. Kiosk hazard.
    const onBlur = () => { keys.current = { f: false, b: false, l: false, r: false, run: false, jump: false } }

    dom.addEventListener('click', onClick)
    document.addEventListener('pointerlockchange', onLockChange)
    document.addEventListener('mousemove', onMouseMove)
    window.addEventListener('keydown', onKeyDown)
    window.addEventListener('keyup', onKeyUp)
    window.addEventListener('blur', onBlur)
    return () => {
      dom.removeEventListener('click', onClick)
      document.removeEventListener('pointerlockchange', onLockChange)
      document.removeEventListener('mousemove', onMouseMove)
      window.removeEventListener('keydown', onKeyDown)
      window.removeEventListener('keyup', onKeyUp)
      window.removeEventListener('blur', onBlur)
      if (document.pointerLockElement === dom) document.exitPointerLock()
      keys.current = { f: false, b: false, l: false, r: false, run: false, jump: false }
      locked.current = false
      aimRef.current = null
      useGameStore.getState().setFpAim(null)
      useGameStore.getState().setSignalBarOpen(false)
    }
  }, [enabled, gl, camera])

  // In a dev build, the walker by hand (window.__walker): where the feet
  // are and which way they face, for checking the monorail's lift, platform
  // and beams without walking the town to them. Like __survey (mainScene)
  useEffect(() => {
    if (process.env.NODE_ENV === 'production' || typeof window === 'undefined') return
    window.__walker = st.current
    return () => { if (window.__walker === st.current) delete window.__walker }
  }, [])

  // The SignalBar's buttons need the player's feet: register a getter while
  // walking (a ref read, so no re-renders ride along).
  useEffect(() => {
    if (!enabled) return
    const g = useGameStore.getState()
    // yaw included so the minimap arrow can show which way you're facing
    g.setPlayerPosGetter(() => ({ x: st.current.x, y: st.current.py, z: st.current.z, yaw: st.current.yaw, grounded: st.current.grounded }))
    return () => g.setPlayerPosGetter(null)
  }, [enabled])

  // Entering first-person: widen the lens, level the camera, seed spawn once.
  // No restore on exit: every camera owner asserts its own lens (the CCTV
  // rig's snap() resets to its base fov), so restoring a captured value here
  // could re-apply a stale mid-rave lens to the rig.
  useEffect(() => {
    if (!enabled) return
    camera.fov = FP_FOV
    camera.up.set(0, 1, 0)
    camera.updateProjectionMatrix()
    if (!st.current.initialized) {
      const over = spawnOverride()
      st.current.x = over ? over.x : SPAWN_X
      st.current.z = over ? over.z : SPAWN_Z
      st.current.yaw = SPAWN_YAW
      st.current.pitch = 0
      st.current.initialized = true
    }
  }, [enabled, camera])

  useFrame((_, dtRaw) => {
    if (!enabled) return
    const dt = Math.min(0.05, dtRaw) // clamp big frame gaps so a stall can't teleport
    const s = st.current

    // Touch look — pixel deltas banked by the thumb on the right half since
    // the last frame. Drained on read so a flick doesn't spin forever.
    const [tdx, tdy] = drainLook()
    if (tdx || tdy) {
      s.yaw -= tdx * TOUCH_LOOK_SENS
      s.pitch = Math.max(-PITCH_MAX, Math.min(PITCH_MAX, s.pitch - tdy * TOUCH_LOOK_SENS))
    }

    // A/D turn, plus the stick's sideways push (analog, so a small lean is a
    // gentle turn rather than a full swing).
    if (keys.current.l) s.yaw += TURN_SPEED * dt
    if (keys.current.r) s.yaw -= TURN_SPEED * dt
    if (touchInput.moveX) s.yaw -= touchInput.moveX * TURN_SPEED * dt

    // Forward on the XZ plane from yaw (camera faces -Z at yaw 0).
    const fx = -Math.sin(s.yaw)
    const fz = -Math.cos(s.yaw)
    const keyF = (keys.current.f ? 1 : 0) - (keys.current.b ? 1 : 0)
    const moveF = Math.max(-1, Math.min(1, keyF + touchInput.moveY))
    const running = keys.current.run && moveF > 0
    const speed = WALK_SPEED * (running ? RUN_MULT : 1)

    let nx = s.x + fx * moveF * speed * dt
    let nz = s.z + fz * moveF * speed * dt
    const ix = nx  // intended position, pre-collision (wall-bump detection)
    const iz = nz

    // Slide against AABB colliders — push out of the least-penetrated edge.
    // Two passes so inside corners resolve cleanly.
    const r = PLAYER_RADIUS
    const lists = [colliders.current, shutDoors(undefined, doors.current)]
    for (let pass = 0; pass < 2; pass++) {
      for (const list of lists) for (const b of list) {
        // A box is only a wall if it stands in the body's way: a top
        // within STEP_UP of the feet is a stair (walk onto it), a bottom
        // above head height is a bridge (walk under it), and a walkable
        // surface blocks nobody at any height.
        if (!b.solid) continue
        if (b.topY <= s.py + STEP_UP) continue
        if (b.bottomY >= s.py + BODY_H) continue
        // Into the box's local frame (undo its rotation), push out of the
        // least-penetrated local edge, rotate the correction back to world.
        const dx = nx - b.cx, dz = nz - b.cz
        const lx = b.ca * dx - b.sa * dz
        const lz = b.sa * dx + b.ca * dz
        const ex = b.hx + r, ez = b.hz + r
        if (lx > -ex && lx < ex && lz > -ez && lz < ez) {
          const dL = lx + ex, dR = ex - lx, dT = lz + ez, dB = ez - lz
          const m = Math.min(dL, dR, dT, dB)
          let clx = lx, clz = lz
          if (m === dL) clx = -ex
          else if (m === dR) clx = ex
          else if (m === dT) clz = -ez
          else clz = ez
          nx = b.cx + (b.ca * clx + b.sa * clz)
          nz = b.cz + (-b.sa * clx + b.ca * clz)
        }
      }
    }

    // Don't walk through Victor.
    const bird = useGameStore.getState().npcStates?.[0]?.pos
    if (bird) {
      const bx = nx - bird.x
      const bz = nz - bird.z
      const bd = Math.hypot(bx, bz)
      const minD = r + KIWI_RADIUS
      if (bd < minD && bd > 1e-4) {
        const push = (minD - bd) / bd
        nx += bx * push
        nz += bz * push
      }
    }

    // Hard world edge: past the visual floor the heightfield flatlines and
    // you'd walk into the fog on invisible ground.
    s.x = Math.max(-WALK_LIMIT, Math.min(WALK_LIMIT, nx))
    s.z = Math.max(-WALK_LIMIT, Math.min(WALK_LIMIT, nz))

    // Wall bump: most of the intended step got eaten by collision. Rising
    // edge only, so grinding along a wall thumps once at first contact
    // rather than pulsing forever.
    const blockedNow = moveF !== 0 &&
      Math.hypot(ix - s.x, iz - s.z) > speed * dt * 0.45
    if (blockedNow && !s.wasBlocked) {
      s.bumpT = BUMP_TIME
      useGameStore.getState().triggerWallBump()
    }
    s.wasBlocked = blockedNow

    // What is under the feet here: the terrain, or the highest box top
    // that is not above the knees. In the air the feet are high, so every
    // top below qualifies and a fall lands on the highest thing under you,
    // which is what makes roofs and ruin walls standable at all.
    let support = 0
    if (terrain) {
      const h = terrain.sample(s.x, s.z)
      if (Number.isFinite(h)) support = h
    }
    for (const b of colliders.current) {
      if (b.topY <= support || b.topY > s.py + STEP_UP) continue
      const dx = s.x - b.cx, dz = s.z - b.cz
      const lx = b.ca * dx - b.sa * dz
      const lz = b.sa * dx + b.ca * dz
      if (Math.abs(lx) < b.hx && Math.abs(lz) < b.hz) support = b.topY
    }
    // the lift's car is a floor that moves: stood in it, you go where it goes
    let car = null
    if (inCar(s.x, s.z)) {
      for (const c of liftsNow()) {
        if (inCar(s.x, s.z) !== c.lf) continue
        if (c.y <= s.py + STEP_UP + 0.6 && c.y > support) { support = c.y; car = c }
      }
    }

    // First frame after spawn: put the feet on the ground, no falling in.
    if (!s.settled) { s.py = support; s.vy = 0; s.grounded = true; s.settled = true }

    // Jump, gravity, landing.
    if (keys.current.jump && s.grounded) {
      s.vy = JUMP_V
      s.grounded = false
      s.fallFrom = s.py
      keys.current.jump = false   // one hop per press, no pogo on hold
    }
    if (s.grounded) {
      if (s.py > support + STEP_UP) {
        // walked off an edge
        s.grounded = false
        s.fallFrom = s.py
        s.vy = 0
      } else {
        // ease onto the new tread so stairs read as steps, not teleports
        s.py += (support - s.py) * Math.min(1, STEP_SMOOTH * dt)
      }
    } else {
      s.vy -= GRAVITY * dt
      s.py += s.vy * dt
      if (s.vy <= 0 && s.py <= support) {
        s.py = support
        s.vy = 0
        s.grounded = true
        if (s.fallFrom - support > LAND_THUD) {
          // a real drop lands with a thud and a step sound
          s.bumpT = BUMP_TIME * 0.7
          playFootstep()
        }
      }
    }

    // riding: the feet go with the floor, not eased after it
    if (car && s.grounded) s.py = car.y

    // Walk head-bob + footsteps. Running swells it; the air stills it.
    let bobY = 0
    let bobRoll = 0
    if (moveF !== 0 && s.grounded) {
      s.bob += dt * speed * BOB_RATE * (running ? RUN_BOB_RATE : 1)
      const sinBob = Math.sin(s.bob)
      bobY = sinBob * BOB_AMP * (running ? RUN_BOB_AMP : 1)
      bobRoll = Math.sin(s.bob * 0.5) * ROLL_AMP * (running ? RUN_ROLL : 1)
      if (sinBob < STEP_TROUGH && s.prevBobSin >= STEP_TROUGH) playFootstep()
      s.prevBobSin = sinBob
    } else {
      s.bob = 0
      s.prevBobSin = 0
    }

    // Breathing: always there, gently, under everything else. Deeper at
    // a run, held for the beat of a jump.
    s.breathT += dt * (running ? RUN_BREATH : 1)
    let breathY = 0
    let breathPitch = 0
    if (s.grounded) {
      const phase = s.breathT * BREATH_HZ * Math.PI * 2
      breathY = Math.sin(phase) * BREATH_AMP * (running ? 1.6 : 1)
      breathPitch = Math.sin(phase + 0.6) * BREATH_PITCH * (running ? 1.8 : 1)
    }

    // Impact shake: a short decaying jolt layered onto the walk bob.
    if (s.bumpT > 0) {
      s.bumpT = Math.max(0, s.bumpT - dt)
      const k = s.bumpT / BUMP_TIME
      bobY += Math.sin(s.bumpT * 90) * 0.04 * k
      bobRoll += Math.sin(s.bumpT * 70) * 0.012 * k
    }

    camera.position.set(s.x, s.py + EYE_HEIGHT + bobY + breathY, s.z)
    camera.rotation.set(s.pitch + breathPitch, s.yaw, bobRoll, 'YXZ')

    // Crosshair scan (throttled ~8Hz): the nearest offering crystal inside a
    // small view cone. Angle test is on the XZ plane, which is where the
    // crystals live; store write only on change.
    s.aimTimer += dt
    if (s.aimTimer >= 0.12) {
      s.aimTimer = 0
      let best = null
      let bestDot = 0
      const ents = useGameStore.getState().entityStates
      for (const e of ents) {
        if (e.type !== 'SACRIFICE') continue
        const dx = e.pos.x - s.x
        const dz = e.pos.z - s.z
        const d = Math.hypot(dx, dz)
        if (d < 0.5 || d > AIM_MAX_DIST) continue
        const dot = (dx / d) * fx + (dz / d) * fz
        const need = d < 4 ? 0.93 : AIM_MIN_DOT
        if (dot > need && dot > bestDot) { best = e; bestDot = dot }
      }
      // The marks bar rides on proximity: near Victor it fades in, walk
      // away and it goes. No key to remember at the exhibition.
      if (FEATURES.signals) {
        const bird = useGameStore.getState().npcStates?.[0]?.pos
        const nearVictor = !!bird && Math.hypot(bird.x - s.x, bird.z - s.z) < 12
        if (useGameStore.getState().signalBarOpen !== nearVictor) {
          useGameStore.getState().setSignalBarOpen(nearVictor)
        }
      }
      const id = best?.id || null
      if ((aimRef.current?.id || null) !== id) {
        aimRef.current = best
          ? { id: best.id, handle: best.state?.handle, phase: best.state?.phase, kind: best.state?.kind }
          : null
        useGameStore.getState().setFpAim(aimRef.current)
      }
    }
  })

  return null
}

export default FirstPersonController
